import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Mail, KeyRound, Eye, EyeOff } from 'lucide-react';
import { loginWithEmail, signUpWithEmail, loginWithGoogle } from '../lib/supabase';
import AgeConsent from './AgeConsent';
import { markAgeConfirmed } from '../lib/ageConsent';

interface AuthModalProps {
  onClose: () => void;
  defaultMode?: 'login' | 'signup';
}

// Generic sign-in/sign-up modal opened from the nav "Sign In" button —
// pops up immediately from wherever the person is, instead of sending them
// to the homepage and making them scroll down to find the form.
export default function AuthModal({ onClose, defaultMode = 'login' }: AuthModalProps) {
  const [mode, setMode] = useState<'signup' | 'login'>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ageOk, setAgeOk] = useState(false);
  const [ageInvalid, setAgeInvalid] = useState(false);

  const handleSubmit = async () => {
    if (!email || !password) { setError('Enter an email and password.'); return; }
    if (mode === 'signup' && !ageOk) { setAgeInvalid(true); return; }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await loginWithEmail(email, password);
        onClose();
      } else {
        markAgeConfirmed();
        const data = await signUpWithEmail(email, password);
        if (data.session) {
          onClose();
        } else {
          setSuccess('Almost there — check your email to confirm your account.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (mode === 'signup' && !ageOk) { setAgeInvalid(true); return; }
    markAgeConfirmed();
    setError('');
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
      setGoogleLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="absolute inset-0"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />

        <motion.div
          className="relative w-full max-w-sm rounded-2xl border border-brand-border overflow-hidden"
          style={{ background: 'var(--color-brand-card)' }}
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 340, damping: 30 }}
        >
          <div
            className="flex items-center justify-between px-5 py-4 border-b border-brand-border"
            style={{ background: 'var(--color-brand-bg)' }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: 'var(--color-brand-accent)' }}>
              {mode === 'signup' ? 'Create your account' : 'Sign in'}
            </span>
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full flex items-center justify-center border border-brand-border transition-colors hover:border-brand-accent text-zinc-500 hover:text-brand-accent"
              style={{ background: 'var(--color-brand-card)' }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="px-6 pt-6 pb-6">
            <div className="flex flex-col gap-4">
              <AgeConsent mode={mode} checked={ageOk} onChange={v => { setAgeOk(v); if (v) setAgeInvalid(false); }} invalid={ageInvalid} />

              <button
                type="button"
                onClick={handleGoogle}
                disabled={googleLoading}
                className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 border border-brand-border transition-all hover:opacity-90 disabled:opacity-50 cursor-pointer"
                style={{ background: 'var(--color-brand-bg)', color: 'var(--color-brand-text)' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.8 2.73v2.27h2.91c1.7-1.57 2.69-3.88 2.69-6.64z" />
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.17l-2.91-2.27c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.34C2.44 15.98 5.48 18 9 18z" />
                  <path fill="#FBBC05" d="M3.95 10.71c-.18-.54-.28-1.11-.28-1.71s.1-1.17.28-1.71V4.95H.96A8.996 8.996 0 000 9c0 1.45.35 2.83.96 4.05l2.99-2.34z" />
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.95l2.99 2.34C4.66 5.16 6.65 3.58 9 3.58z" />
                </svg>
                {googleLoading ? 'Redirecting...' : 'Continue with Google'}
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px" style={{ background: 'var(--color-brand-border)' }} />
                <span className="text-xs text-zinc-500">or</span>
                <div className="flex-1 h-px" style={{ background: 'var(--color-brand-border)' }} />
              </div>

              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl text-sm focus:outline-none focus:border-brand-accent border border-brand-border"
                  style={{ background: 'var(--color-brand-bg)', color: 'var(--color-brand-text)' }}
                />
              </div>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  className="w-full pl-10 pr-10 py-3 rounded-xl text-sm focus:outline-none focus:border-brand-accent border border-brand-border"
                  style={{ background: 'var(--color-brand-bg)', color: 'var(--color-brand-text)' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {error && <p className="text-rose-400 text-sm">{error}</p>}
              {success && <p className="text-emerald-400 text-sm">{success}</p>}

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full py-3 bg-brand-accent text-white rounded-xl font-bold hover:opacity-90 transition-all disabled:opacity-50 cursor-pointer"
              >
                {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
              </button>

              <button
                onClick={() => { setMode(m => (m === 'signup' ? 'login' : 'signup')); setError(''); setSuccess(''); }}
                className="text-zinc-500 text-xs text-center hover:text-zinc-300 transition-colors"
              >
                {mode === 'signup' ? 'Already have an account? Sign in' : "New here? Create an account"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
